import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppConfigService } from '@core/appconfig.service';
import { EmailApiService } from '@core/services/application/email/email-api.service';
import { ApiResponse } from '@models/api-response';
import { ApplicationUser } from '@models/application-user';
import {
  Classroom,
  ClassroomReportHeaderRequest,
  IClassroom,
  IClassroomCreate,
  IClassroomReportData,
  IClassroomUpdate
} from '@models/classroom';
import { ClassroomIcon } from '@models/classroom-icon';
import { ClassroomProducts } from '@models/classroom-products';
import { ClassroomProfile } from '@models/classroom-profile';
import { FocusModeStudentsPatch } from '@models/focus-mode';
import { Group } from '@models/group';
import { KeyValuePair } from '@models/key-value-pair';
import { ILicense } from '@models/license/license';
import { LicenseAssignee } from '@models/license/license-assignee';
import { SchoolLicense } from '@models/license/school-license';
import { Paged } from '@models/paged-params';
import { IPagedResponse, PagedResponse } from '@models/paged-response';
import { UnlockedContent } from '@models/unlocked-content';
import { IUser, UserPartial } from '@models/user';
import { LicenseAssignmentType, LicenseStatus } from '@shared/enums/license-status';
import { RoleType } from '@shared/enums/role-type';
import { Helpers } from '@shared/helpers';
import { copyArray, copyObject } from '@shared/zb-object-helper/object-helper';
import {
  instantiateApiResponseFromArray,
  instantiatePagedResponseFromArray
} from '@shared/zb-rxjs-operators/rxjs-operators';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ClassroomSearchParams } from 'src/app/classrooms/classes/classroom/classroom.service.model';

/**
 * The classroom service works within the context of the classroom id set on the classroomId property.
 * The classroom service is intended to share data across components related to a single classroom.
 */
@Injectable()
export class ClassroomService {

  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private emailApiService: EmailApiService
  ) {
  }

  private buildLoadClassroomUsersUrl(classroomId: string, roleTypes: string[] = []): string {
    const searchUrl = roleTypes.reduce((url: string, roleType: string) => `${url}roleType=${roleType}&`,
      `${this.appConfig.apiUrl}/classroom/${classroomId}/user?`);
    return searchUrl.replace(/[&?]$/, '');
  }


  private buildStudentSearchUrl(userParams: KeyValuePair[]): string {
    const params = [{ key: 'roleTypes', value: 'Student' }].concat(userParams);
    const searchUrl = params.reduce((url: string, param: KeyValuePair) => `${url}${param.key}=${param.value}&`,
      `${this.appConfig.apiUrl}/user/search?`);
    return searchUrl.replace(/[&?]$/, '');
  }

  private buildLisSyncUrl(classroomId: string): string {
    return `/dpd-api/3/classroom/${classroomId}/lis.json`;
  }

  private getClassroomUrl(classroomId: string): string {
    return `${this.appConfig.apiUrl}/classroom/${classroomId}`;
  }

  private getEducationalUnitUrl(schoolId: string): string {
    return `${this.appConfig.apiUrl}/educational-unit/${schoolId}`;
  }

  private getClassroomUpdateUrl(schoolId: string, classroomId: string): string {
    return `${this.getEducationalUnitUrl(schoolId)}/classroom/${classroomId}`;
  }

  private getClassroomLicenseUrl(schoolId: string, classroomId?: string, schoolYear?: string): string {
    let url = classroomId
      ? `${this.getEducationalUnitUrl(schoolId)}/license?assignmentType=${LicenseAssignmentType.Classroom}&classroomId=${classroomId}`
      : `${this.getEducationalUnitUrl(schoolId)}/license?assignmentType=${LicenseAssignmentType.Classroom}`;

    if (schoolYear) {
      url += `&schoolYear=${schoolYear}`;
    }

    return url;
  }

  private getSearchClassroomsUrl(): string {
    return `${this.appConfig.apiUrl}/classroom`;
  }

  private getSearchSingleClassroomUrl(classroomId: string): string {
    return `${this.getSearchClassroomsUrl()}?classroomId=${classroomId}&includeProfile=true`;
  }

  private getClassroomProfileUrl(classroomId: string): string {
    return `${this.getClassroomUrl(classroomId)}/profile`;
  }

  private getClassroomMembershipUrl(classroomId: string): string {
    return `${this.getClassroomUrl(classroomId)}/user`;
  }

  private getClassroomResendEmailUrl(classroomId: string): string {
    // For some reason the action doesn't exist as part of classroom endpoints.
    return `${this.appConfig.apiUrl}/application/email/authenticated/resend-parent-activation-emails/${classroomId}`;
  }

  private getClassroomIconUrl(): string {
    return `${this.appConfig.apiUrl}/classroom/icon`;
  }

  private getUnlockedContentUrl(classroomId: string, productType: string, variantType: string, topicKey: string): string {
    return `${this.appConfig.apiUrl}/classroom/${classroomId}/unlocked-activities?productType=${productType}&variantType=${variantType}&topicKey=${topicKey}`;
  }

  private getFocusModeStudentsUrl(classroomId: string, productType: string, variantType: string, topicKey: string): string {
    return `${this.appConfig.apiUrl}/classroom/${classroomId}/focus-mode?productType=${productType}&variantType=${variantType}&topicKey=${topicKey}`;
  }

  // API assumes there's only 1 product with FocusMode Capability added to a classroom.
  private getFocusModeStudentsPatchUrl(classroomId: string): string {
    return `${this.appConfig.apiUrl}/classroom/${classroomId}/focus-mode`;
  }

  private getClassroomStudentGroupUrl(classroomGroupId: string) {
    return `${this.appConfig.apiUrl}/classroom/student-group/${classroomGroupId}`;
  }

  /**
  * Gets list of members for this classroom
  */
  loadClassroomUsers(classroomId: string, roleTypes: string[] = []): Observable<IUser[]> {
    return this.http.get<ApiResponse<IUser[]>>(this.buildLoadClassroomUsersUrl(classroomId, roleTypes))
      .pipe(
        map((res) => {
          const response = res.response.map(u => ApplicationUser.fromSearch(u));
          return response;
        }),
      );
  }

  /**
   * Searches for classrooms and returns the non-paged response.
   *
   * If you need paged data, see {@link searchForClassroomsPaged}.
   */
  searchForClassrooms(search: ClassroomSearchParams): Observable<ApiResponse<Classroom[]>> {
    const params: HttpParams = Helpers.objToQueryParams(search);
    return this.http.get(this.getSearchClassroomsUrl(), { params }).pipe(
      instantiateApiResponseFromArray(Classroom),
    );
  }

  /**
   * Searches for classrooms and returns the paged response.
   *
   * If you don't need paged data, see {@link searchForClassrooms}.
   */
  searchForClassroomsPaged(search: Paged<ClassroomSearchParams>): Observable<IPagedResponse<Classroom[]>> {
    const params = Helpers.objToQueryParams(search);
    return this.http.get(this.getSearchClassroomsUrl(), { params }).pipe(
      instantiatePagedResponseFromArray(Classroom)
    );
  }

  loadStudents(classroomId: string): Observable<IUser[]> {
    const params = [
      { key: 'activeOnly', value: 'true' },
      { key: 'pageNumber', value: '1' },
      { key: 'pageSize', value: '40' },
      { key: 'classroomId', value: classroomId },
    ];
    return this.http.get<IPagedResponse<IUser[]>>(this.buildStudentSearchUrl(params))
      .pipe(
        map(res => new PagedResponse<IUser[]>(true, res)),
        map(res => res.response.map(values => ApplicationUser.fromSearch(values))),
      );
  }

  /*
  * Gets unassigned licenses
  */
  getAvailableLicenses(schoolId: string): Observable<ApiResponse<SchoolLicense[]>> {
    return this.http.get<ApiResponse<ILicense[]>>(
      `${this.getClassroomLicenseUrl(schoolId)}&licenseFilterType=ShowUnassigned`
    )
      .pipe(
        map((res: ApiResponse<ILicense[]>) => new ApiResponse<SchoolLicense[]>(true, {
          response: res.response.map(v => copyObject(v, SchoolLicense)),
          messages: res.messages,
        })),
      );
  }

  createStudentPasswords(classroomId: string): Observable<ApiResponse<UserPartial[]>> {
    const url = `${this.getClassroomUrl(classroomId)}/student-password`;
    return this.http.patch<ApiResponse<UserPartial[]>>(url, {})
      .pipe(
        map((res) => {
          const success = res.response.length > 0;
          return new ApiResponse<UserPartial[]>(success, {
            response: res.response,
            messages: res.messages,
          });
        }),
      );
  }

  /**
   * gets the licenses currently assigned to a class
   */
  getClassroomProductLicenses(schoolId: string, classroomId: string, schoolYear?: string, showPastYearLicenses: boolean = false): Observable<ApiResponse<SchoolLicense[]>> {
    const url = this.getClassroomLicenseUrl(schoolId, classroomId, schoolYear);
    const params = { showPastYearLicenses };
    return this.http.get(url, { params })
      .pipe(
        map((res: ApiResponse<ILicense[]>) => new ApiResponse<SchoolLicense[]>(true, {
          response: res.response.map(v => copyObject(v, SchoolLicense)),
          messages: res.messages,
        })),
      );
  }


  /**
   * returns an observable of the ClassroomProducts object which details what products belong to a classroom
   * and what products remain available to add to a classroom.
   */
  getClassroomProducts(classroom: Classroom): Observable<ClassroomProducts> {
    return forkJoin([
      this.getAvailableLicenses(classroom.educationalUnitId),
      this.getClassroomProductLicenses(classroom.educationalUnitId, classroom.classroomId)
    ]).pipe(
      map(([availableResult, assignedResult]) => {
        const assigned = assignedResult.success && assignedResult.response?.length > 0 ? assignedResult.response : [];
        const available = availableResult.success && availableResult.response?.length > 0
          ? availableResult.response.filter(availableLicense => (
            !(assigned?.find(lic => lic && lic.skuId === availableLicense.skuId))))
          : [];
        if (available && assigned) {
          const cp = new ClassroomProducts(assigned, available);
          return cp;
        }
        return null;
      })
    );
  }

  buildClassroomProducts(licenses: ILicense[]): ClassroomProducts {
    const available = licenses
      .filter(license => license.status === LicenseStatus.Available)
      .map(v => copyObject(v, SchoolLicense));
    const assigned = licenses
      .filter(license => license.status === LicenseStatus.AssignedToYou)
      .map(v => copyObject(v, SchoolLicense));
    const cp = new ClassroomProducts(assigned, available);
    return cp;
  }

  updateClassroomProductLicenses(classroom: Classroom, licensesToUpdate: LicenseAssignee[]): Observable<ApiResponse<SchoolLicense[]>> {
    const url = this.getClassroomLicenseUrl(classroom.educationalUnitId, classroom.classroomId);
    return this.http.patch(url, licensesToUpdate)
      .pipe(
        map((res: ApiResponse<ILicense[]>) => new ApiResponse<SchoolLicense[]>(true, {
          response: res.response.map(v => copyObject(v, SchoolLicense)),
          messages: res.messages,
        })),
      );
  }

  studentSearch(page: number = 1, search: string = null): Observable<PagedResponse<IUser[]>> {
    const params = [
      { key: 'activeOnly', value: 'true' },
      { key: 'pageNumber', value: page },
      { key: 'pageSize', value: '40' },
    ];
    if (search) {
      params.push({ key: 'userSearchCriteria', value: search });
    }
    return this.http
      .get<IPagedResponse<IUser[]>>(this.buildStudentSearchUrl(params))
      .pipe(
        map(res => new PagedResponse<IUser[]>(true, res)),
        map((res) => {
          res.response = res.response.map(u => ApplicationUser.fromSearch(u));
          return res;
        }),
      );
  }

  addStudentToClassroom(user: IUser, classroomId: string): Observable<ApiResponse<boolean>> {
    const data = { userIds: [user.userId], roleToAssign: RoleType.Student };
    return this.http.patch<ApiResponse<string[]>>(this.getClassroomMembershipUrl(classroomId), data)
      .pipe(
        map(res => new ApiResponse<boolean>(true, { response: true, messages: res.messages })),
      );
  }

  removeStudentFromClassroom(userId: string, classroomId: string): Observable<ApiResponse<boolean>> {
    return this.http
      .request('delete', this.getClassroomMembershipUrl(classroomId), {
        body: { userIds: [userId], userIntegrationIds: [] },
      })
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
      );
  }

  canAttemptSync(): boolean {
    return false;
  }

  /**
   * @deprecated use {@link searchForClassrooms} or {@link searchForClassroomsPaged} instead.
   */
  getClassrooms(classroomId: string, showPastYearLicenses: boolean = false, includeClassroomGroups: boolean = false): Observable<ApiResponse<Classroom[]>> {
    const params = { showPastYearLicenses, includeClassroomGroups };
    return this.http.get<ApiResponse<IClassroom[]>>(this.getSearchSingleClassroomUrl(classroomId), { params })
      .pipe(
        map((res) => {
          const success = res.response.length > 0;
          const classrooms: Classroom[] = copyArray(res.response, Classroom);
          classrooms.forEach(classroom => classroom.profile.sortProfiles());
          return new ApiResponse<Classroom[]>(success, {
            response: classrooms,
            messages: res.messages,
          });
        }),
      );
  }

  getClassroomReportHeaders(classroomReportHeaderRequest: ClassroomReportHeaderRequest): Observable<ApiResponse<IClassroomReportData>> {
    const url = `${this.appConfig.apiUrl}/classroom-header`;
    return this.http.post<ApiResponse<IClassroomReportData>>(url, classroomReportHeaderRequest)
      .pipe(
        shareReplay(),
        map(res => new ApiResponse<IClassroomReportData>(true, res)),
      );
  }

  addClassroom(newClassroom: IClassroomCreate, schoolId: string): Observable<ApiResponse<Classroom>> {
    return this.http.post<ApiResponse<IClassroom>>(`${this.getEducationalUnitUrl(schoolId)}/classroom`, newClassroom)
      .pipe(
        map(res => new ApiResponse<Classroom>(true, {
          response: copyObject({ ...res.response }, Classroom),
          messages: res.messages,
        })),
      );
  }

  createGroup(classroomId: string, groupName: string, studentIdsToAdd: string[]): Observable<ApiResponse<Group>> {
    return this.http.post<ApiResponse<IClassroom>>(`${this.getClassroomUrl(classroomId)}/student-group`, {
      groupName,
      studentIdsToAdd
    })
      .pipe(
        map(res => new ApiResponse<Group>(true, {
          response: res.response,
          messages: res.messages,
        })),
      );
  }

  updateGroup(studentGroupId: string, groupName: string, studentIdsToAdd: string[], studentIdsToRemove: string[]): Observable<ApiResponse<Group>> {
    return this.http.patch<ApiResponse<IClassroom>>(this.getClassroomStudentGroupUrl(studentGroupId),
      { groupName, studentIdsToAdd, studentIdsToRemove })
      .pipe(
        map(res => new ApiResponse<Group>(true, {
          response: res.response,
          messages: res.messages,
        })),
      );
  }

  deleteGroup(studentGroupId: string): Observable<ApiResponse<boolean>> {
    return this.http.request('delete', this.getClassroomStudentGroupUrl(studentGroupId))
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
      );
  }

  updateClassroom(classroom: IClassroomUpdate, schoolId: string): Observable<ApiResponse<Classroom>> {
    const url = this.getClassroomUpdateUrl(schoolId, classroom.classroomId);
    return this.http.patch<ApiResponse<IClassroom>>(url, classroom)
      .pipe(
        map(res => new ApiResponse<Classroom>(true, {
          response: copyObject({ ...res.response }, Classroom),
          messages: [],
        })),
      );
  }

  deleteClassroom(classroomId: string): Observable<ApiResponse<boolean>> {
    return this.http.request('delete', this.getClassroomUrl(classroomId))
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
      );
  }

  saveClassroomProfile(classroomId: string, profile: ClassroomProfile): Observable<ApiResponse<ClassroomProfile>> {
    return this.http.patch<ApiResponse<any>>(this.getClassroomProfileUrl(classroomId), profile)
      .pipe(
        map(res => new ApiResponse<ClassroomProfile>(true, { response: res.response, messages: res.messages })),
      );
  }

  resendParentEmails(classroomId: string): Observable<ApiResponse<boolean>> {
    return this.emailApiService.resendParentEmails(classroomId)
      .pipe(
        map(() => new ApiResponse<boolean>(true, { response: true, messages: [] })),
      );
  }

  getClassroomIcons(): Observable<ApiResponse<ClassroomIcon[]>> {
    return this.http.get(this.getClassroomIconUrl())
      .pipe(
        map(res => new ApiResponse<ClassroomIcon[]>(true, res)),
      );
  }

  getUnlockedContent(classroomId: string, productType: string, variantType: string, topicKey: string): Observable<ApiResponse<UnlockedContent[]>> {
    const url = this.getUnlockedContentUrl(classroomId, productType, variantType, topicKey);
    return this.http.get(url).pipe(
      instantiateApiResponseFromArray(UnlockedContent),
    );
  }

  getStudentGroup(classroomGroupId: string): Observable<ApiResponse<Group>> {
    const url = this.getClassroomStudentGroupUrl(classroomGroupId);
    return this.http.get<ApiResponse<Group>>(url)
      .pipe(
        map(data => new ApiResponse<Group>(true, data)),
      );
  }

  getFocusModeStudents(classroomId: string, productType: string, variantType: string, topicKey: string): Observable<ApiResponse<UserPartial[]>> {
    const url = this.getFocusModeStudentsUrl(classroomId, productType, variantType, topicKey);
    return this.http.get(url).pipe(
      map(data => new ApiResponse<UserPartial[]>(true, data)),
    );
  }

  createFocusModeStudents(classroomId: string, students: FocusModeStudentsPatch): Observable<ApiResponse<UserPartial[]>> {
    const url = `${this.getFocusModeStudentsPatchUrl(classroomId)}`;
    return this.http.patch<ApiResponse<UserPartial[]>>(url, students)
      .pipe(
        map(res => new ApiResponse<UserPartial[]>(true, {
          response: res.response,
          messages: res.messages,
        })),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(new ApiResponse<UserPartial[]>(false, err.error));
        })
      );
  }
}
